import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Alert, Badge, Table, Modal, ProgressBar, Tab, Tabs } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPrint,
    faUsers,
    faIdCard,
    faCheck,
    faTimes,
    faExclamationTriangle,
    faList,
    faCalculator,
    faCog,
    faHistory,
    faDownload,
    faPlay,
    faStop,
    faClock
} from '@fortawesome/free-solid-svg-icons';

// Import des services
import { beneficiarySearchService } from '../../../services/BeneficiarySearchService';
import { cardPrintingService } from '../../../services/CardPrintingService';

const PrintingWithLifecycleManager = () => {
    const [activeTab, setActiveTab] = useState('selection');
    const [loading, setLoading] = useState(false);

    // États pour la sélection
    const [selectionMethod, setSelectionMethod] = useState('beneficiaries');
    const [beneficiaryIds, setBeneficiaryIds] = useState('');
    const [policeNumber, setPoliceNumber] = useState('');
    const [familyId, setFamilyId] = useState('');

    // États pour les paramètres d'impression
    const [printingParams, setPrintingParams] = useState({
        templateId: 'MODERN_BLUE',
        cardStockId: 1,
        printOptions: {
            copies: 1,
            printBothSides: true,
            printerName: ''
        },
        layout: {
            cardsPerRow: 2,
            cardsPerColumn: 5
        }
    });

    // États pour la validation
    const [validationResult, setValidationResult] = useState(null);
    const [stockValidation, setStockValidation] = useState(null);

    // États pour l'impression
    const [printJob, setPrintJob] = useState(null);
    const [showPrintModal, setShowPrintModal] = useState(false);

    // États des données
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [stockRanges, setStockRanges] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [printers, setPrinters] = useState([]);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            // Simulation des appels API
            await Promise.all([
                loadStockRanges(),
                loadTemplates(),
                loadPrinters()
            ]);
        } catch (error) {
            console.error('Erreur chargement données initiales:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadStockRanges = async () => {
        try {
            const API_BASE_URL = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
            const response = await fetch(`${API_BASE_URL}/Stock/summary`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const stockData = await response.json();
                // Transform stock data to expected format
                const ranges = stockData.map(stock => ({
                    id: stock.id,
                    startSerial: '1000000', // Default - should come from API
                    endSerial: '1099999', // Default - should come from API
                    totalQuantity: stock.currentQuantity || 0,
                    usedQuantity: 0,
                    availableQuantity: stock.currentQuantity || 0,
                    batchNumber: stock.productCode || 'DEFAULT',
                    isActive: stock.isActive
                }));
                setStockRanges(ranges);
            } else {
                console.warn('Stock API failed, no fallback data');
                setStockRanges([]);
            }
        } catch (error) {
            console.error('Error loading stock ranges:', error);
            setStockRanges([]);
        }
    };

    const loadTemplates = async () => {
        try {
            const templatesData = await cardPrintingService.getCardTemplates();
            setTemplates(templatesData);
        } catch (error) {
            console.error('Error loading templates:', error);
            setTemplates([]);
        }
    };

    const loadPrinters = async () => {
        try {
            const printersData = await cardPrintingService.getAvailablePrinters();
            setPrinters(printersData);
        } catch (error) {
            console.warn('Printers API not available yet, using empty list:', error.message);
            setPrinters([]);
        }
    };

    const validateSelection = async () => {
        setLoading(true);
        setValidationResult(null);

        try {
            let beneficiaryResults = [];

            switch (selectionMethod) {
                case 'beneficiaries':
                    const ids = beneficiaryIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
                    if (ids.length === 0) {
                        setValidationResult({
                            isValid: false,
                            errors: ['Veuillez saisir au moins un ID de bénéficiaire']
                        });
                        return;
                    }
                    beneficiaryResults = await beneficiarySearchService.searchByBeneficiaryIds(ids);
                    break;

                case 'police':
                    if (!policeNumber || policeNumber.trim() === '') {
                        setValidationResult({
                            isValid: false,
                            errors: ['Veuillez saisir un numéro de police']
                        });
                        return;
                    }
                    beneficiaryResults = await beneficiarySearchService.searchByPoliceNumber(policeNumber.trim());
                    break;

                case 'family':
                    if (!familyId || familyId.trim() === '') {
                        setValidationResult({
                            isValid: false,
                            errors: ['Veuillez saisir un numéro de famille']
                        });
                        return;
                    }
                    beneficiaryResults = await beneficiarySearchService.searchByFamilyNumber(familyId.trim());
                    break;

                default:
                    setValidationResult({
                        isValid: false,
                        errors: ['Méthode de sélection non valide']
                    });
                    return;
            }

            if (beneficiaryResults.length === 0) {
                setValidationResult({
                    isValid: false,
                    errors: ['Aucun bénéficiaire trouvé avec les critères fournis']
                });
                return;
            }

            // Vérifier les restrictions pour chaque bénéficiaire
            const enrichedBeneficiaries = [];
            for (const beneficiary of beneficiaryResults) {
                try {
                    const restrictions = await beneficiarySearchService.checkBeneficiaryRestrictions(beneficiary.id);
                    enrichedBeneficiaries.push({
                        ...beneficiary,
                        restrictions: restrictions,
                        hasRestrictions: restrictions.length > 0,
                        isValid: beneficiary.isValid && restrictions.length === 0
                    });
                } catch (error) {
                    console.warn(`Erreur vérification restrictions pour ${beneficiary.id}:`, error.message);
                    enrichedBeneficiaries.push({
                        ...beneficiary,
                        restrictions: [],
                        hasRestrictions: false,
                        isValid: beneficiary.isValid
                    });
                }
            }

            const validCount = enrichedBeneficiaries.filter(b => b.isValid).length;
            const restrictedCount = enrichedBeneficiaries.filter(b => b.hasRestrictions).length;

            setBeneficiaries(enrichedBeneficiaries);

            setValidationResult({
                isValid: validCount > 0,
                totalRequested: beneficiaryResults.length,
                validBeneficiaries: validCount,
                invalidBeneficiaries: beneficiaryResults.length - validCount,
                restrictedBeneficiaries: restrictedCount,
                errors: validCount === 0 ? ['Aucun bénéficiaire valide trouvé'] : [],
                warnings: [
                    ...(validCount < beneficiaryResults.length ? [`${beneficiaryResults.length - validCount} bénéficiaires non valides ignorés`] : []),
                    ...(restrictedCount > 0 ? [`${restrictedCount} bénéficiaires avec restrictions détectées`] : [])
                ]
            });

            if (validCount > 0) {
                await validateStock(validCount);
            }

        } catch (error) {
            console.error('Erreur validation:', error);
            const errorMessage = beneficiarySearchService.handleError(error);
            setValidationResult({
                isValid: false,
                totalRequested: 0,
                validBeneficiaries: 0,
                invalidBeneficiaries: 0,
                restrictedBeneficiaries: 0,
                errors: [errorMessage.message || 'Erreur technique lors de la validation'],
                warnings: []
            });
            setBeneficiaries([]);
        } finally {
            setLoading(false);
        }
    };

    const validateStock = async (cardCount) => {
        try {
            // Appel API réel pour valider le stock
            const stockValidationResult = await cardPrintingService.validateStockForPrinting(
                cardCount,
                printingParams.cardStockId
            );

            // Allocation des numéros de série via API
            const serialAllocationResult = await cardPrintingService.allocateSerialNumbers(
                printingParams.cardStockId,
                cardCount,
                'CARD_PRINTING_PREVIEW',
                localStorage.getItem('userName') || 'Admin'
            );

            setStockValidation({
                isValid: stockValidationResult.isValid && serialAllocationResult.isSuccess,
                cardCount: cardCount,
                selectedRange: stockRanges.find(range => range.id === printingParams.cardStockId),
                requirements: stockValidationResult.stockRequirements || [],
                allocatedSerials: serialAllocationResult.allocatedSerials || [],
                totalCost: stockValidationResult.totalCost || 0,
                errors: [
                    ...(stockValidationResult.errors || []),
                    ...(serialAllocationResult.validationErrors || [])
                ],
                warnings: [
                    ...(stockValidationResult.warnings || []),
                    ...(serialAllocationResult.warnings || [])
                ],
                stockValidationDetails: stockValidationResult,
                serialAllocationDetails: serialAllocationResult
            });

        } catch (error) {
            console.warn('Erreur validation stock:', error.message);
            setStockValidation({
                isValid: false,
                cardCount: cardCount,
                selectedRange: null,
                requirements: [],
                allocatedSerials: [],
                totalCost: 0,
                errors: [error.message || 'Erreur technique lors de la validation du stock'],
                warnings: []
            });
        }
    };

    const startPrinting = async () => {
        if (!validationResult?.isValid || !stockValidation?.isValid) return;

        setLoading(true);
        setShowPrintModal(true);

        try {
            const validBeneficiaryIds = beneficiaries
                .filter(b => b.isValid)
                .map(b => b.id);

            const printRequest = {
                templateId: printingParams.templateId,
                beneficiaryIds: validBeneficiaryIds,
                cardStockId: printingParams.cardStockId,
                printedBy: localStorage.getItem('userName') || 'Admin',
                printOptions: printingParams.printOptions,
                layout: printingParams.layout,
                validateStockBeforePrinting: true,
                allocateSerialsBeforePrinting: true
            };

            console.log('Démarrage impression avec cycle de vie complet:', printRequest);

            // Appel API réel pour démarrer l'impression
            const printingResult = await cardPrintingService.startPrintingWithLifecycle(printRequest);

            if (!printingResult.success) {
                throw new Error(printingResult.message || 'Échec du démarrage de l\'impression');
            }

            // Initialiser le job avec les vraies données
            setPrintJob({
                jobId: printingResult.jobId,
                batchId: printingResult.batchId,
                status: 'STARTED',
                progress: 0,
                startTime: new Date(),
                requestedCardCount: validBeneficiaryIds.length,
                currentPhase: 'Validation des bénéficiaires',
                phases: [
                    'Validation des bénéficiaires',
                    'Allocation des numéros de série',
                    'Création des entités cartes',
                    'Mise en production',
                    'Génération des images',
                    'Impression physique',
                    'Finalisation'
                ]
            });

            // Surveiller le progrès en temps réel
            await cardPrintingService.monitorPrintingProgress(
                printingResult.jobId,
                (progressUpdate) => {
                    setPrintJob(prev => ({
                        ...prev,
                        ...progressUpdate,
                        completionTime: progressUpdate.status === 'COMPLETED' ? new Date() : prev.completionTime
                    }));
                }
            );

            // Récupérer le résultat final depuis l'API
            const finalJobStatus = await cardPrintingService.getJobStatus(printingResult.jobId);

            setPrintJob(prev => ({
                ...prev,
                completionTime: new Date(),
                result: {
                    isSuccess: finalJobStatus?.status === 'COMPLETED',
                    message: finalJobStatus?.message || `Impression ${finalJobStatus?.status === 'COMPLETED' ? 'réussie' : 'échouée'} - ${validBeneficiaryIds.length} cartes`,
                    printedCardCount: finalJobStatus?.printedCardCount || validBeneficiaryIds.length,
                    allocatedSerials: finalJobStatus?.allocatedSerials || stockValidation.allocatedSerials,
                    createdCardIds: finalJobStatus?.createdCardIds || [],
                    batchId: printingResult.batchId,
                    jobDetails: finalJobStatus
                }
            }));

        } catch (error) {
            console.error('Erreur impression:', error);
            setPrintJob(prev => ({
                ...prev,
                status: 'FAILED',
                result: {
                    isSuccess: false,
                    message: 'Erreur lors de l\'impression',
                    errors: [error.message]
                }
            }));
        } finally {
            setLoading(false);
        }
    };

    const canProceedToPrint = validationResult?.isValid && stockValidation?.isValid;

    const renderSelectionTab = () => (
        <Row>
            <Col md={8}>
                <Card>
                    <Card.Header>
                        <h5>Sélection des Bénéficiaires</h5>
                    </Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Méthode de sélection</Form.Label>
                            <div>
                                <Form.Check
                                    type="radio"
                                    label="Par IDs de bénéficiaires"
                                    name="selectionMethod"
                                    value="beneficiaries"
                                    checked={selectionMethod === 'beneficiaries'}
                                    onChange={(e) => setSelectionMethod(e.target.value)}
                                    className="mb-2"
                                />
                                <Form.Check
                                    type="radio"
                                    label="Par numéro de police"
                                    name="selectionMethod"
                                    value="police"
                                    checked={selectionMethod === 'police'}
                                    onChange={(e) => setSelectionMethod(e.target.value)}
                                    className="mb-2"
                                />
                                <Form.Check
                                    type="radio"
                                    label="Par ID de famille"
                                    name="selectionMethod"
                                    value="family"
                                    checked={selectionMethod === 'family'}
                                    onChange={(e) => setSelectionMethod(e.target.value)}
                                />
                            </div>
                        </Form.Group>

                        {selectionMethod === 'beneficiaries' && (
                            <Form.Group className="mb-3">
                                <Form.Label>IDs des bénéficiaires (séparés par des virgules)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={beneficiaryIds}
                                    onChange={(e) => setBeneficiaryIds(e.target.value)}
                                    placeholder="2001, 2002, 2003, 2004..."
                                />
                            </Form.Group>
                        )}

                        {selectionMethod === 'police' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Numéro de police</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={policeNumber}
                                    onChange={(e) => setPoliceNumber(e.target.value)}
                                    placeholder="POL123456"
                                />
                            </Form.Group>
                        )}

                        {selectionMethod === 'family' && (
                            <Form.Group className="mb-3">
                                <Form.Label>ID de famille</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={familyId}
                                    onChange={(e) => setFamilyId(e.target.value)}
                                    placeholder="FAM_001"
                                />
                            </Form.Group>
                        )}

                        <Button
                            variant="primary"
                            onClick={validateSelection}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Validation...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                                    Valider la sélection
                                </>
                            )}
                        </Button>
                    </Card.Body>
                </Card>
            </Col>

            <Col md={4}>
                <Card>
                    <Card.Header>
                        <h6>Paramètres d'impression</h6>
                    </Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Modèle de carte</Form.Label>
                            <Form.Control
                                as="select"
                                value={printingParams.templateId}
                                onChange={(e) => setPrintingParams({
                                    ...printingParams,
                                    templateId: e.target.value
                                })}
                            >
                                {templates.map(template => (
                                    <option key={template.id} value={template.id}>
                                        {template.name}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Plage de numéros de série</Form.Label>
                            <Form.Control
                                as="select"
                                value={printingParams.cardStockId}
                                onChange={(e) => setPrintingParams({
                                    ...printingParams,
                                    cardStockId: parseInt(e.target.value)
                                })}
                            >
                                {stockRanges.map(range => (
                                    <option key={range.id} value={range.id}>
                                        {range.batchNumber} ({range.availableQuantity} disponibles)
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Imprimante</Form.Label>
                            <Form.Control
                                as="select"
                                value={printingParams.printOptions.printerName}
                                onChange={(e) => setPrintingParams({
                                    ...printingParams,
                                    printOptions: {
                                        ...printingParams.printOptions,
                                        printerName: e.target.value
                                    }
                                })}
                            >
                                <option value="">Imprimante par défaut</option>
                                {printers.map(printer => (
                                    <option key={printer.name} value={printer.name} disabled={printer.status === 'busy'}>
                                        {printer.name} ({printer.status === 'ready' ? 'Prête' : 'Occupée'})
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );

    const renderValidationResults = () => (
        <>
            {validationResult && (
                <Card className="mb-3">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Résultat de validation</h6>
                            {validationResult.isValid ? (
                                <Badge bg="success">Validation réussie</Badge>
                            ) : (
                                <Badge bg="danger">Validation échouée</Badge>
                            )}
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3} className="text-center">
                                <h4 className="text-primary">{validationResult.totalRequested}</h4>
                                <small>Demandés</small>
                            </Col>
                            <Col md={3} className="text-center">
                                <h4 className="text-success">{validationResult.validBeneficiaries}</h4>
                                <small>Valides</small>
                            </Col>
                            <Col md={2} className="text-center">
                                <h4 className="text-warning">{validationResult.invalidBeneficiaries || 0}</h4>
                                <small>Invalides</small>
                            </Col>
                            <Col md={2} className="text-center">
                                <h4 className="text-danger">{validationResult.restrictedBeneficiaries || 0}</h4>
                                <small>Restrictions</small>
                            </Col>
                            <Col md={2} className="text-center">
                                <h4 className="text-info">
                                    {stockValidation ? stockValidation.allocatedSerials?.length || 0 : 0}
                                </h4>
                                <small>Numéros alloués</small>
                            </Col>
                        </Row>

                        {validationResult.errors.length > 0 && (
                            <Alert variant="danger" className="mt-3">
                                <strong>Erreurs :</strong>
                                <ul className="mb-0">
                                    {validationResult.errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        {validationResult.warnings.length > 0 && (
                            <Alert variant="warning" className="mt-3">
                                <strong>Avertissements :</strong>
                                <ul className="mb-0">
                                    {validationResult.warnings.map((warning, index) => (
                                        <li key={index}>{warning}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                    </Card.Body>
                </Card>
            )}

            {stockValidation && (
                <Card className="mb-3">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Validation du stock</h6>
                            {stockValidation.isValid ? (
                                <Badge bg="success">Stock disponible</Badge>
                            ) : (
                                <Badge bg="danger">Stock insuffisant</Badge>
                            )}
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <h6>Besoins en matériel :</h6>
                        <Table size="sm" className="mb-3">
                            <thead>
                                <tr>
                                    <th>Matériel</th>
                                    <th>Requis</th>
                                    <th>Disponible</th>
                                    <th>Coût</th>
                                    <th>Statut</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stockValidation.requirements?.map((req, index) => (
                                    <tr key={index}>
                                        <td>{req.productName}</td>
                                        <td>{req.required}</td>
                                        <td>{req.available}</td>
                                        <td>{req.cost.toFixed(2)} €</td>
                                        <td>
                                            {req.available >= req.required ? (
                                                <Badge bg="success">OK</Badge>
                                            ) : (
                                                <Badge bg="danger">Manquant</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <div className="d-flex justify-content-between align-items-center">
                            <strong>Coût total estimé : {stockValidation.totalCost?.toFixed(2)} €</strong>
                            {canProceedToPrint && (
                                <Button variant="success" onClick={startPrinting} disabled={loading}>
                                    <FontAwesomeIcon icon={faPrint} className="me-2" />
                                    Lancer l'impression
                                </Button>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Tableau détaillé des bénéficiaires */}
            {beneficiaries && beneficiaries.length > 0 && (
                <Card className="mb-3">
                    <Card.Header>
                        <h6 className="mb-0">
                            <FontAwesomeIcon icon={faUsers} className="me-2" />
                            Liste des bénéficiaires ({beneficiaries.length})
                        </h6>
                    </Card.Header>
                    <Card.Body>
                        <Table size="sm" responsive>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Nom complet</th>
                                    <th>N° Assuré</th>
                                    <th>Police</th>
                                    <th>Famille</th>
                                    <th>Statut</th>
                                    <th>Restrictions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {beneficiaries.map((beneficiary, index) => (
                                    <tr key={index} className={!beneficiary.isValid ? 'table-warning' : ''}>
                                        <td>{beneficiary.id}</td>
                                        <td>
                                            <strong>{beneficiary.name} {beneficiary.firstName}</strong>
                                        </td>
                                        <td>{beneficiary.numeroAssuré}</td>
                                        <td>{beneficiary.policeNumber}</td>
                                        <td>{beneficiary.familyId}</td>
                                        <td>
                                            {beneficiary.isValid ? (
                                                <Badge bg="success">Valide</Badge>
                                            ) : beneficiary.hasRestrictions ? (
                                                <Badge bg="danger">Restrictions</Badge>
                                            ) : (
                                                <Badge bg="warning">Invalide</Badge>
                                            )}
                                        </td>
                                        <td>
                                            {beneficiary.hasRestrictions ? (
                                                <div>
                                                    {beneficiary.restrictions.slice(0, 2).map((restriction, rIndex) => (
                                                        <Badge key={rIndex} bg="outline-danger" className="me-1 mb-1">
                                                            {restriction.restrictionType || restriction.type || 'Restriction'}
                                                        </Badge>
                                                    ))}
                                                    {beneficiary.restrictions.length > 2 && (
                                                        <Badge bg="outline-secondary">
                                                            +{beneficiary.restrictions.length - 2} autres
                                                        </Badge>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-muted">Aucune</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}
        </>
    );

    return (
        <div className="printing-lifecycle-manager">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>
                    <FontAwesomeIcon icon={faPrint} className="me-2" />
                    Impression avec Gestion de Cycle de Vie
                </h3>
            </div>

            <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
                <Tab eventKey="selection" title="Sélection">
                    {renderSelectionTab()}
                </Tab>

                <Tab eventKey="validation" title="Validation" disabled={!validationResult}>
                    {renderValidationResults()}
                </Tab>

                <Tab eventKey="beneficiaries" title={`Bénéficiaires (${beneficiaries.length})`} disabled={beneficiaries.length === 0}>
                    <Card>
                        <Card.Header>
                            <h6>Liste des bénéficiaires sélectionnés</h6>
                        </Card.Header>
                        <Card.Body>
                            <Table striped hover>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Nom</th>
                                        <th>Police</th>
                                        <th>Famille</th>
                                        <th>Statut</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {beneficiaries.map(beneficiary => (
                                        <tr key={beneficiary.id} className={!beneficiary.isValid ? 'table-warning' : ''}>
                                            <td>{beneficiary.id}</td>
                                            <td>{beneficiary.name} {beneficiary.firstName}</td>
                                            <td>{beneficiary.policeNumber}</td>
                                            <td>{beneficiary.familyId}</td>
                                            <td>
                                                {beneficiary.isValid ? (
                                                    <Badge bg="success">Valide</Badge>
                                                ) : (
                                                    <Badge bg="warning">Invalide</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>

            {/* Modal de progression d'impression */}
            <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} backdrop="static" centered>
                <Modal.Header closeButton={printJob?.status === 'COMPLETED' || printJob?.status === 'FAILED'}>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faPrint} className="me-2" />
                        Impression en cours - {printJob?.jobId}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {printJob && (
                        <div>
                            <div className="text-center mb-4">
                                <ProgressBar
                                    now={printJob.progress}
                                    label={`${printJob.progress}%`}
                                    className="mb-2"
                                    animated={printJob.status === 'IN_PROGRESS'}
                                />
                                <Badge bg={printJob.status === 'COMPLETED' ? 'success' : printJob.status === 'FAILED' ? 'danger' : 'primary'}>
                                    {printJob.currentPhase}
                                </Badge>
                            </div>

                            <div className="mb-3">
                                <h6>Phases du processus :</h6>
                                {printJob.phases.map((phase, index) => (
                                    <div key={index} className={`d-flex align-items-center mb-1 ${phase === printJob.currentPhase ? 'text-primary fw-bold' : ''}`}>
                                        <FontAwesomeIcon
                                            icon={phase === printJob.currentPhase ? faPlay : index < printJob.phases.indexOf(printJob.currentPhase) ? faCheck : faClock}
                                            className="me-2"
                                        />
                                        {phase}
                                    </div>
                                ))}
                            </div>

                            {printJob.result && (
                                <Alert variant={printJob.result.isSuccess ? 'success' : 'danger'}>
                                    <h6>Résultat :</h6>
                                    <p>{printJob.result.message}</p>
                                    {printJob.result.isSuccess && (
                                        <div>
                                            <p><strong>Cartes imprimées :</strong> {printJob.result.printedCardCount}</p>
                                            <p><strong>Numéros alloués :</strong> {printJob.result.allocatedSerials?.slice(0, 3).join(', ')}
                                                {printJob.result.allocatedSerials?.length > 3 && '...'}
                                            </p>
                                        </div>
                                    )}
                                </Alert>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {printJob?.status === 'COMPLETED' || printJob?.status === 'FAILED' ? (
                        <Button variant="primary" onClick={() => setShowPrintModal(false)}>
                            Fermer
                        </Button>
                    ) : (
                        <Button variant="secondary" disabled>
                            Impression en cours...
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PrintingWithLifecycleManager;